// Libs
import React, { useEffect, useState } from 'react';
import { Tooltip, Modal } from 'antd';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import * as Yup from 'yup';

// Own Libs
import { Button, FormContext, FormDatePicker, FormSelect, FormSubmitButton, FormTextArea, MasterDetailForm, Row } from '@digital/ui';

//Api
import { anexarCadastroResultadoAvaliacaoFinanceira, ISasPropostaOrdemCompra, ISasPropostaOrdemCompraDocs } from '@app/api/sas/sasProposta';

//Files
import { editandoState, modalAcoesIdState, reloadVendorListState, vendorListDocsState } from '../atoms';
import { UploadFile, Visibility } from '@app/components';

//Utils
import { isObjNotEmpty, vtObjToFormData } from '@app/helpers/objHelper';
import { optionsResultadoAnalise } from '@app/helpers/optionsSwitch';
import { EStatusCadastroResultadoAvaliacaoFinanceira } from '@app/enums/EResultadoEnvioConfide';
import moment from 'moment';
import ModalConfirmContentJustificativa from '@app/components/ModalConfirmContent/ModalConfirmContentJustificativa';
import { IArquivo } from '@app/types/core.types';

interface Props {
    sasProposta: ISasPropostaOrdemCompra;
}

export const ModalCadastroResultadoAvaliacaoFinanceira = ({ sasProposta }: Props) => {
    const setModalAcoesId = useSetRecoilState(modalAcoesIdState);
    const editando = useRecoilValue(editandoState);
    const vendorListDocs = useRecoilValue(vendorListDocsState);

    const [exibirModal, setExibirModal] = useState(false);
    const [doc, setDoc] = useState<ISasPropostaOrdemCompraDocs>({} as ISasPropostaOrdemCompraDocs);

    useEffect(() => {
        var doc = vendorListDocs.find(f => f.id == sasProposta.id);
        if (isObjNotEmpty(doc)) {
            setDoc(doc || {} as ISasPropostaOrdemCompraDocs);
        }
    }, [exibirModal]);

    const handleCancel = () => {
        setExibirModal(false);
    };

    const handleCancelAndClose = () => {
        setExibirModal(false);
        setModalAcoesId('');
    };

    const handleSubmit = (values: any) => {
        const form = vtObjToFormData(values);

        if (editando.sasPropostaId == sasProposta.id) {
            Modal.confirm({
                title: <>{editando.justificativa}</>,
                content: <ModalConfirmContentJustificativa
                    texto='É necessário informar a justificativa para a alteração.'
                    setJustificativa={e => {
                        form.set('justificativaEdicao', e);
                    }}
                />,
                okText: 'Confirmar',
                cancelText: 'Cancelar',
                onOk: async (e) => {
                    form.append('editando', 'true');
                    return new Promise((resolve, reject) => {
                        anexarCadastroResultadoAvaliacaoFinanceira(sasProposta.id, form)
                            .then((data: any) => {
                                if (data && data.valid) {
                                    handleCancelAndClose();
                                    return resolve(true);
                                } else {
                                    if (data.notifications && data.notifications.length > 0) {
                                        const erroJustificativaEdicao = data.notifications.findIndex((c: any) => {
                                            return c.property == 'justificativaEdicao';
                                        });
                                        if (erroJustificativaEdicao >= 0) {
                                            return reject(false);
                                        }
                                    }
                                }
                            });
                    });
                },
                onCancel: handleCancelAndClose
            });
        } else {
            anexarCadastroResultadoAvaliacaoFinanceira(sasProposta.id, form)
                .then((data: any) => {
                    if (data && data.valid) {
                        handleCancelAndClose();
                    }
                });
        }
    };

    return (
        <>
            <Tooltip title="Cadastro do Resultado da Avaliação Financeira">
                <Button icon='business-set_as_resume' onClick={() => { setExibirModal(true); }} />
            </Tooltip>
            <Visibility visible={exibirModal}>

                <Modal
                    title={`Cadastro do Resultado da Avaliação Financeira`}
                    width='780px'
                    visible={exibirModal}
                    onCancel={handleCancel}
                    footer={null} >
                    <Visibility visible={isObjNotEmpty(sasProposta) && exibirModal} >
                        <MasterDetailForm
                            key={`formEnvioConfide_${sasProposta.id}`}
                            actions={[
                                <Button title='fechar' type='link' onClick={() => handleCancel()} />,
                                <Visibility visible={
                                    editando.sasPropostaId === sasProposta.id || sasProposta.statusCadastroResultadoAvaliacaoFinanceira !== EStatusCadastroResultadoAvaliacaoFinanceira.RECEBIDO
                                }>
                                    <FormSubmitButton title='Salvar' type='primary' noIcon onClick={handleSubmit} />
                                </Visibility>
                            ]}
                            initialValues={sasProposta}
                            validationSchema={
                                Yup.object().shape({
                                    arquivo: isObjNotEmpty(doc) && doc.anexoResultadoAvaliacaoFinanceira ? Yup.mixed().notRequired() : Yup.mixed().required('É necessário anexar um arquivo.'),
                                })
                            }
                        >
                            <FormContext.Consumer>
                                {({ formProps }) => (
                                    <>
                                        <Row>
                                            <FormSelect
                                                label='Status'
                                                name='statusCadastroResultadoAvaliacaoFinanceira'
                                                value={sasProposta.statusCadastroResultadoAvaliacaoFinanceira
                                                    ? sasProposta.statusCadastroResultadoAvaliacaoFinanceira
                                                    : EStatusCadastroResultadoAvaliacaoFinanceira.PENDENTE
                                                }
                                                options={[
                                                    { text: EStatusCadastroResultadoAvaliacaoFinanceira.PENDENTE, value: EStatusCadastroResultadoAvaliacaoFinanceira.PENDENTE },
                                                    { text: EStatusCadastroResultadoAvaliacaoFinanceira.RECEBIDO, value: EStatusCadastroResultadoAvaliacaoFinanceira.RECEBIDO },
                                                ]}
                                                span={6}
                                                disabled
                                            />

                                            <FormDatePicker
                                                format='DD/MM/YYYY'
                                                name='dataRecebimentoCadastroResultadoAvaliacaoFinanceira'
                                                label='Data do recebimento do parecer da Área Financeira'
                                                required
                                                disabled={editando.sasPropostaId !== sasProposta.id && sasProposta.statusCadastroResultadoAvaliacaoFinanceira === EStatusCadastroResultadoAvaliacaoFinanceira.RECEBIDO}
                                                span={6} />

                                            <FormDatePicker
                                                format='DD/MM/YYYY'
                                                name='validadeCadastroResultadoAvaliacaoFinanceira'
                                                label='Validade'
                                                value={formProps && formProps.values.dataRecebimentoCadastroResultadoAvaliacaoFinanceira &&
                                                    moment(formProps.values.dataRecebimentoCadastroResultadoAvaliacaoFinanceira).add(12, 'month')}
                                                disabled
                                                span={6} />

                                            <FormTextArea
                                                name='comentarioCadastroResultadoAvaliacaoFinanceira'
                                                label='Comentário'
                                                disabled={editando.sasPropostaId !== sasProposta.id && sasProposta.statusCadastroResultadoAvaliacaoFinanceira === EStatusCadastroResultadoAvaliacaoFinanceira.RECEBIDO}
                                                span={12}
                                            />


                                            <FormSelect
                                                label='Resultado da Análise'
                                                name="resultadoCadastroResultadoAvaliacaoFinanceira"
                                                options={optionsResultadoAnalise}
                                                span={6}
                                                disabled={editando.sasPropostaId !== sasProposta.id && sasProposta.statusCadastroResultadoAvaliacaoFinanceira === EStatusCadastroResultadoAvaliacaoFinanceira.RECEBIDO}
                                                required
                                            />
                                        </Row>
                                        <br></br>
                                        <Row>
                                            <UploadFile
                                                idDoComponente='arquivo-ModalStatusCadastroResultadoAvaliacaoFinanceira'
                                                type='primary'
                                                hasFile={false}
                                                hasLabel={true}
                                                icon='ui-upload'
                                                span={6}
                                                onGetArquivo={() => {

                                                    if (isObjNotEmpty(doc.anexoResultadoAvaliacaoFinanceira)) {
                                                        return Promise.resolve({
                                                            id: doc.anexoResultadoAvaliacaoFinanceira.id,
                                                            nome: doc.anexoResultadoAvaliacaoFinanceira.nome,
                                                            status: "",
                                                            statusArquivoId: doc.anexoResultadoAvaliacaoFinanceira.statusArquivoId || "",
                                                        } as IArquivo);
                                                    }
                                                    return Promise.resolve(undefined);

                                                }}
                                                showDownload={true}
                                            />
                                        </Row>
                                    </>
                                )}
                            </FormContext.Consumer>

                        </MasterDetailForm>
                    </Visibility>
                </Modal>

            </Visibility>
        </>
    );
};